'use client';
import React, { FC, useEffect, useRef, useMemo } from 'react';
import { CurrentItems, AllFbtItems } from 'services/fbtItems/types';
import { subTotalResponse } from 'services/subTotal/types';
import CurrentCard from './current-card';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import FbtSubtotalAction from '../fbt-subtotal';
import FbtCard from './fbt-card';
import { FBTMobileDrawerProvider } from '../fbt-mobile-drawer/fbt-mobile-drawer-context';
import { FBT_LIST_NAME } from '../../../constants/constants';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { CartResponse } from 'services/cart/types';
import Styles from './fbt-grid.module.scss';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';

type MergedProductPrice = ProductDealerPriceAvailability & {
  type: string;
  name: string;
  imageURL: string;
};

interface FbtGridProps {
  allFbtItems: AllFbtItems[];
  currentItem: CurrentItems;
  currency: string;
  partsPriceData: ProductDealerPriceAvailability[];
  isCatCorp: boolean;
  children: React.ReactNode;
  subTotal: subTotalResponse;
  setSubTotal: (arg: {
    formattedSubtotal: string;
    unformattedSubtotal: number;
  }) => void;
  totalLoading: boolean;
  setTotalLoading: (arg: boolean) => void;
  itemSummary: ISummary;
  storeLocationId: string;
  handleCheckboxChange?: (
    e: CustomEvent<{
      checked: boolean;
      indeterminate: boolean;
      value: string;
    }>
  ) => void;
  cartDetails: CartResponse;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const FbtGrid: FC<FbtGridProps> = ({
  allFbtItems,
  currentItem,
  currency,
  partsPriceData,
  isCatCorp,
  subTotal,
  setSubTotal,
  totalLoading,
  setTotalLoading,
  handleCheckboxChange,
  children,
  itemSummary,
  storeLocationId,
  cartDetails,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const cartId = cartDetails?.orderHeader;
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const hasViewListItem = useRef(false);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const getData = (partNumber: string, data) => {
    const getPart = data?.filter((item) => {
      return partNumber === item.partNumber;
    });
    return getPart?.length > 0 && getPart[0];
  };

  const mergedFbtResponse = useMemo(() => {
    if (isCatCorp || !partsPriceData || !itemSummary?.products) {
      return [];
    }
    return partsPriceData.map((item) => {
      const fbtResponse = itemSummary.products.find((priceItem) => {
        return priceItem.partNumber === item.partNumber;
      });
      return fbtResponse
        ? { ...item, ...fbtResponse }
        : {
            ...item,
            manufacturer: itemSummary.products[0]?.manufacturer
          };
    });
  }, [
    partsPriceData,
    itemSummary?.products,
    isCatCorp
  ]) as MergedProductPrice[];

  const returnFbtCard = (fbtItem: AllFbtItems) => {
    const partPrice = getData(fbtItem.partNumber, partsPriceData);
    const manufacturerItem = getData(fbtItem.partNumber, itemSummary?.products);
    return (
      <div
        className="col-12 col-lg-2 col-md-4 col-sm-1 cat-u-padding-sm"
        key={fbtItem?.partNumber}
      >
        <FbtCard
          product={fbtItem}
          partPrice={partPrice}
          currency={currency}
          isCatCorp={isCatCorp}
          handleCheckboxChange={handleCheckboxChange}
          storeLocationId={storeLocationId}
          orderItem={cartId}
          manufacturerItem={manufacturerItem}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
        >
          {children}
        </FbtCard>
      </div>
    );
  };

  useEffect(() => {
    const fbtViewItemList = {
      item_list_id: FBT_LIST_NAME,
      item_list_name: FBT_LIST_NAME,
      currency: currency
    };
    const viewReplacePayload = {
      products: mergedFbtResponse as MergedProductPrice[],
      locationId: storeLocationId,
      itemListData: fbtViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true
    };

    if (isMD && mergedFbtResponse?.length > 0 && !hasViewListItem.current) {
      fireViewItemListEvent(viewReplacePayload);
      hasViewListItem.current = true;
    }
  }, [
    cartDetails,
    storeLocationId,
    fireViewItemListEvent,
    mergedFbtResponse,
    isMD,
    currency
  ]);

  return (
    <div className="container">
      <div className={`row ${Styles['c-fbt-card']}`}>
        <div
          className={`col-12 col-lg-2 col-md-4 col-sm-1 cat-u-padding-sm  ${Styles['c-fbt-card__current-item']}`}
        >
          <CurrentCard
            currentItem={currentItem}
            partPrice={getData(currentItem.partNumber, partsPriceData)}
            manufacturerItem={getData(
              currentItem.partNumber,
              itemSummary?.products
            )}
            currency={currency}
            isCatCorp={isCatCorp}
            handleCheckboxChange={handleCheckboxChange}
            storeLocationId={storeLocationId}
            orderItem={cartId}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
          >
            {children}
          </CurrentCard>
        </div>
        {(allFbtItems ?? [])?.map((fbtItem) => returnFbtCard(fbtItem))}
      </div>
      <FBTMobileDrawerProvider>
        <FbtSubtotalAction
          partsPriceData={partsPriceData}
          subTotal={subTotal}
          currency={currency}
          currentItem={currentItem}
          setSubTotal={setSubTotal}
          totalLoading={totalLoading}
          setTotalLoading={setTotalLoading}
          itemSummary={itemSummary}
          storeLocationId={storeLocationId}
          cartId={cartId?.orderId}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
        />
      </FBTMobileDrawerProvider>
    </div>
  );
};

export default FbtGrid;
